/**
 * AccessibilityWidget.tsx — Floating Accessibility Controls
 *
 * 12-feature accessibility panel for BuzzBitX.
 * Renders as a floating button (bottom-left; see useTriggerPlacement for when
 * it moves) that opens a settings panel.
 * Persists preferences in localStorage. Fully keyboard-accessible.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X,
  RotateCcw,
  Type,
  Contrast,
  Link2,
  Eye,
  PauseCircle,
  MousePointer2,
  SpaceIcon,
  Palette,
  ImageOff,
  Keyboard,
  ScanLine,
  Accessibility,
  PersonStanding,
} from 'lucide-react';

import {
  DEFAULT_PREFS,
  loadPrefs,
  savePrefs,
  upsertStyle,
  removeStyle,
  ensureReadingGuide,
  ensureDyslexicLink,
  STYLE_HIGH_CONTRAST,
  STYLE_HIGHLIGHT_LINKS,
  STYLE_STOP_ANIM,
  STYLE_BIG_CURSOR,
  STYLE_TEXT_SPACING,
  STYLE_GRAYSCALE,
  STYLE_INVERT,
  STYLE_HIDE_IMAGES,
  STYLE_KBD_NAV,
  type A11yPrefs,
} from './accessibilityWidgetParts/core';
import { ToggleRow } from './accessibilityWidgetParts/ToggleRow';
import { TRIGGER_POSITION, useTriggerPlacement } from './accessibilityWidgetParts/useTriggerPlacement';

/* ────────────────────── Component ────────────────────── */

export const AccessibilityWidget: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState<A11yPrefs>(loadPrefs);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const placement = useTriggerPlacement();

  /* ── Apply all styles whenever prefs change ── */
  useEffect(() => {
    const root = document.documentElement;

    // 1. Font size
    root.style.fontSize = prefs.fontSize === 100 ? '' : `${prefs.fontSize}%`;

    // 2. High contrast
    if (prefs.highContrast) {
      upsertStyle(
        STYLE_HIGH_CONTRAST,
        `body:not(#a11y-widget-panel):not(#a11y-widget-panel *){background:#000!important;color:#fff!important;}
         body *:not(#a11y-widget-panel):not(#a11y-widget-panel *):not([class*="a11y-widget"]){background-color:#000!important;color:#fff!important;border-color:#fff!important;}`,
      );
    } else removeStyle(STYLE_HIGH_CONTRAST);

    // 3. Highlight links
    if (prefs.highlightLinks) {
      upsertStyle(
        STYLE_HIGHLIGHT_LINKS,
        `a:not(#a11y-widget-panel a){text-decoration:underline!important;outline:2px solid #18181B!important;outline-offset:2px!important;}`,
      );
    } else removeStyle(STYLE_HIGHLIGHT_LINKS);

    // 4. Dyslexia font
    if (prefs.dyslexiaFont) {
      ensureDyslexicLink();
      root.style.fontFamily = '"OpenDyslexic", sans-serif';
    } else {
      document.getElementById('a11y-dyslexic-font')?.remove();
      root.style.fontFamily = '';
    }

    // 5. Stop animations
    if (prefs.stopAnimations) {
      upsertStyle(
        STYLE_STOP_ANIM,
        `*,*::before,*::after{animation-duration:0s!important;animation-delay:0s!important;transition-duration:0s!important;transition-delay:0s!important;scroll-behavior:auto!important;}`,
      );
    } else removeStyle(STYLE_STOP_ANIM);

    // 6. Reading guide (managed via mousemove listener below)
    const guide = ensureReadingGuide();
    guide.style.display = prefs.readingGuide ? 'block' : 'none';

    // 7. Big cursor
    if (prefs.bigCursor) {
      upsertStyle(
        STYLE_BIG_CURSOR,
        `*:not(#a11y-widget-panel *){cursor:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48'%3E%3Cpath d='M8 4l28 20H20l-4 16z' fill='black' stroke='white' stroke-width='2'/%3E%3C/svg%3E") 8 4,auto!important;}`,
      );
    } else removeStyle(STYLE_BIG_CURSOR);

    // 8. Text spacing
    if (prefs.textSpacing) {
      upsertStyle(
        STYLE_TEXT_SPACING,
        `*:not(#a11y-widget-panel *){letter-spacing:.12em!important;word-spacing:.16em!important;line-height:1.8!important;}`,
      );
    } else removeStyle(STYLE_TEXT_SPACING);

    // 9. Grayscale
    if (prefs.grayscale) {
      upsertStyle(STYLE_GRAYSCALE, `html{filter:grayscale(100%)!important;}`);
    } else removeStyle(STYLE_GRAYSCALE);

    // 10. Invert colors
    if (prefs.invertColors) {
      upsertStyle(
        STYLE_INVERT,
        `html{filter:invert(100%) hue-rotate(180deg)!important;}
         #a11y-widget-panel,#a11y-widget-panel *{filter:invert(100%) hue-rotate(180deg)!important;}`,
      );
    } else removeStyle(STYLE_INVERT);

    // 11. Hide images
    if (prefs.hideImages) {
      upsertStyle(
        STYLE_HIDE_IMAGES,
        `img:not(#a11y-widget-panel img),svg:not(#a11y-widget-panel svg),video,picture{opacity:0!important;visibility:hidden!important;}`,
      );
    } else removeStyle(STYLE_HIDE_IMAGES);

    // 12. Keyboard navigation
    if (prefs.keyboardNav) {
      upsertStyle(
        STYLE_KBD_NAV,
        `*:focus{outline:3px solid #18181B!important;outline-offset:2px!important;}`,
      );
    } else removeStyle(STYLE_KBD_NAV);

    // Persist
    savePrefs(prefs);
  }, [prefs]);

  /* ── Reading-guide mousemove ── */
  useEffect(() => {
    if (!prefs.readingGuide) return;

    const guide = ensureReadingGuide();
    const onMove = (e: MouseEvent) => {
      guide.style.top = `${e.clientY}px`;
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [prefs.readingGuide]);

  /* ── Focus trap + Escape ── */
  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      if (e.key === 'Tab' && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll<HTMLElement>(
          'button,input[type="range"],a,[tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener('keydown', onKey);
    // Auto-focus first control
    const timer = setTimeout(() => {
      panelRef.current?.querySelector<HTMLElement>('button,input')?.focus();
    }, 50);
    return () => {
      window.removeEventListener('keydown', onKey);
      clearTimeout(timer);
    };
  }, [open]);

  /* ── Updater helpers ── */
  const toggle = useCallback(
    (key: keyof Omit<A11yPrefs, 'fontSize'>) =>
      setPrefs((p) => ({ ...p, [key]: !p[key] })),
    [],
  );

  const resetAll = useCallback(() => {
    setPrefs({ ...DEFAULT_PREFS });
    // Clean up root inline styles
    document.documentElement.style.fontSize = '';
    document.documentElement.style.fontFamily = '';
  }, []);

  return (
    <>
      {/* ── Floating trigger button ── */}
      <button
        ref={triggerRef}
        onClick={() => setOpen((o) => !o)}
        aria-label="Open accessibility settings"
        data-placement={placement}
        className={`fixed ${TRIGGER_POSITION[placement]} z-50 h-12 bg-gray-900 hover:bg-gray-800 text-white shadow-lg flex items-center justify-center transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 focus:ring-offset-slate-900`}
      >
        {/* The circled standing figure is the conventional a11y-widget mark. */}
        <PersonStanding className="w-6 h-6" />
      </button>

      {/* ── Panel ── */}
      {open && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/40"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />

          <div
            ref={panelRef}
            id="a11y-widget-panel"
            role="dialog"
            aria-label="Accessibility settings"
            aria-modal="true"
            className="a11y-widget fixed bottom-36 left-5 z-50 w-80 max-h-[70vh] bg-slate-900 border border-slate-700 rounded-xl shadow-lg flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
              <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                <Accessibility className="w-4 h-4 text-gray-500" />
                Accessibility
              </h2>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close accessibility panel"
                className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1">
              {/* Font size slider */}
              <div className="px-3 py-2.5">
                <div className="flex items-center gap-3 text-sm text-slate-300 mb-2">
                  <Type className="w-4 h-4 shrink-0" />
                  <span className="flex-1">Font Size</span>
                  <span className="text-xs text-gray-500 font-mono">{prefs.fontSize}%</span>
                </div>
                <input
                  type="range"
                  min={100}
                  max={200}
                  step={10}
                  value={prefs.fontSize}
                  onChange={(e) =>
                    setPrefs((p) => ({ ...p, fontSize: Number(e.target.value) }))
                  }
                  aria-label="Font size"
                  className="w-full h-1.5 bg-slate-700 rounded-full appearance-none cursor-pointer accent-gray-900"
                />
              </div>

              <ToggleRow
                icon={Contrast}
                label="High Contrast"
                ariaLabel="Toggle high contrast mode"
                active={prefs.highContrast}
                onToggle={() => toggle('highContrast')}
              />
              <ToggleRow
                icon={Link2}
                label="Highlight Links"
                ariaLabel="Toggle link highlighting"
                active={prefs.highlightLinks}
                onToggle={() => toggle('highlightLinks')}
              />
              <ToggleRow
                icon={Eye}
                label="Dyslexia Font"
                ariaLabel="Toggle dyslexia-friendly font"
                active={prefs.dyslexiaFont}
                onToggle={() => toggle('dyslexiaFont')}
              />
              <ToggleRow
                icon={PauseCircle}
                label="Stop Animations"
                ariaLabel="Toggle stop animations"
                active={prefs.stopAnimations}
                onToggle={() => toggle('stopAnimations')}
              />
              <ToggleRow
                icon={ScanLine}
                label="Reading Guide"
                ariaLabel="Toggle reading guide"
                active={prefs.readingGuide}
                onToggle={() => toggle('readingGuide')}
              />
              <ToggleRow
                icon={MousePointer2}
                label="Big Cursor"
                ariaLabel="Toggle large cursor"
                active={prefs.bigCursor}
                onToggle={() => toggle('bigCursor')}
              />
              <ToggleRow
                icon={SpaceIcon}
                label="Text Spacing"
                ariaLabel="Toggle increased text spacing"
                active={prefs.textSpacing}
                onToggle={() => toggle('textSpacing')}
              />
              <ToggleRow
                icon={Palette}
                label="Grayscale"
                ariaLabel="Toggle grayscale mode"
                active={prefs.grayscale}
                onToggle={() => toggle('grayscale')}
              />
              <ToggleRow
                icon={Contrast}
                label="Invert Colors"
                ariaLabel="Toggle color inversion"
                active={prefs.invertColors}
                onToggle={() => toggle('invertColors')}
              />
              <ToggleRow
                icon={ImageOff}
                label="Hide Images"
                ariaLabel="Toggle hide images"
                active={prefs.hideImages}
                onToggle={() => toggle('hideImages')}
              />
              <ToggleRow
                icon={Keyboard}
                label="Keyboard Navigation"
                ariaLabel="Toggle keyboard focus outlines"
                active={prefs.keyboardNav}
                onToggle={() => toggle('keyboardNav')}
              />
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-slate-700 flex items-center justify-between">
              <a
                href="/accessibility-statement"
                className="text-xs text-gray-500 hover:text-gray-300 underline"
              >
                Accessibility Statement
              </a>
              <button
                onClick={resetAll}
                aria-label="Reset all accessibility settings"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <RotateCcw className="w-3 h-3" />
                Reset All
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default AccessibilityWidget;
